import { useEffect, useMemo, useRef, useState } from 'react';
import { useGT } from 'gt-react';
import {
  CaretRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XIcon,
  BrainIcon,
  WrenchIcon,
  CheckCircleIcon,
  XCircleIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import { cn } from 'src/lib/utils';
import { revealInScroller } from './revealInScroller';
import { FilterChip, StatusDot, SpanTypeIcon, LivePill } from './atoms';
import { duplicateSpanIds, fmtTokens, flattenSpans, filterTree } from './utils';
import type {
  Span,
  SpanBrief,
  SpanHumanTakeover,
  FlatRow,
  TraceFilters,
  SpanType,
  SpanStatus,
} from './types';
import { sopModeClass } from 'src/components/common/sopReplayCore';
import { SpanScreenshotButton } from './SpanScreenshotButton';
import { SpanDuration, SpanStartTime } from './SpanTiming';
import type { RunClock } from './spanClock';

// --- Row parts, shared by the table row and the compact list row ---
//
// The two renderers differ in LAYOUT (fixed-height table cells vs a variable-height flex block),
// not in what a span looks like. Keeping the shared pieces here is what stops the compact mode
// from being a second, drifting copy of the row's identity.

/** The expand/collapse triangle, or a spacer keeping leaf rows aligned with their siblings. */
function SpanCaret({
  row,
  isExpanded,
  onToggle,
  gt,
}: {
  row: FlatRow;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  gt: ReturnType<typeof useGT>;
}) {
  if (!row.hasChildren) return <span className="w-4 shrink-0" />;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle(row.id);
      }}
      // Keyboard needs its own guard: Enter/Space on this button raises a `keydown` that reaches
      // the treeitem's handler BEFORE the browser synthesizes the click, so stopping propagation
      // on click alone would still expand the row and open the detail sheet in one keystroke.
      onKeyDown={(e) => e.stopPropagation()}
      aria-label={
        isExpanded
          ? gt('Collapse {name}', {
              context:
                'accessible label to collapse a trace span’s nested steps',
              name: row.name,
            })
          : gt('Expand {name}', {
              context: 'accessible label to expand a trace span’s nested steps',
              name: row.name,
            })
      }
      className="w-4 h-4 flex items-center justify-center border-none bg-transparent text-stone-500 cursor-pointer p-0 shrink-0 transition-transform duration-100"
      style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
    >
      <CaretRightIcon className="h-2.5 w-2.5" />
    </button>
  );
}

/** A replayed SOP step's mode badge — `healed` drifted and was re-aimed, `failed` couldn't be
 * reproduced. The single most useful thing on a procedure's row, so it sits inline with the name
 * rather than in the detail panel. */
function SopModeBadge({ mode }: { mode: string }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded border px-1 py-px font-mono text-[9.5px] uppercase tracking-wide',
        sopModeClass(mode),
      )}
    >
      {mode}
    </span>
  );
}

/** Whether a replayed step's declared post-condition held. */
function SopVerifyBadge({
  verify,
  gt,
}: {
  verify: NonNullable<Span['sopVerify']>;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <span
      className="shrink-0"
      title={
        verify.passed
          ? gt('Verify post-condition held', {
              context: 'tooltip: a replayed step’s Verify check passed',
            })
          : gt('Verify post-condition did not hold', {
              context: 'tooltip: a replayed step’s Verify check failed',
            })
      }
    >
      {verify.passed ? (
        <CheckCircleIcon
          weight="fill"
          className="h-3 w-3 text-emerald-600"
          aria-hidden
        />
      ) : (
        <XCircleIcon
          weight="fill"
          className="h-3 w-3 text-red-600"
          aria-hidden
        />
      )}
    </span>
  );
}

/** Icon + name + the action's argument + SOP badges: a span's identity, however it's laid out. */
// Cyclomatic 10 here is eight OPTIONAL badges (detail chip, camera, model, SOP mode, verify, …);
// the branches ARE the row's identity. Well under the cyclomatic ceiling of 20 — only the
// estimated-coverage term of CRAP puts it over 30.
// fallow-ignore-next-line complexity
function SpanLabel({
  row,
  query,
  showModel,
  gt,
  onSelectScreenshot,
}: {
  row: FlatRow;
  query: string;
  /** The wide table has room for the model id; the narrow rail doesn't. */
  showModel: boolean;
  gt: ReturnType<typeof useGT>;
  onSelectScreenshot?: (span: Span) => void;
}) {
  const nameMatches =
    query && row.name.toLowerCase().includes(query.toLowerCase());
  // A human's row names the person's act, not a tool; its chip says how the pause ended.
  const name = row.human ? humanRowName(gt) : row.name;
  const detail = row.human ? humanOutcomeLabel(row.human, gt) : row.detail;
  return (
    <>
      <SpanTypeIcon
        type={row.type}
        hasChildren={row.hasChildren}
        isAgent={!!row.agentCard || !!row.isSubagent}
        isHuman={!!row.human}
        action={row.action}
      />
      <span
        className={cn(
          'truncate font-mono text-[12px] font-medium',
          nameMatches && 'bg-amber-200/50 px-0.5 rounded',
        )}
      >
        {name}
      </span>
      {detail && (
        <span
          className={cn(
            'shrink-0 truncate max-w-[220px] rounded px-1 font-mono text-[10.5px]',
            row.human
              ? 'bg-amber-100 text-amber-900'
              : 'bg-stone-100 text-stone-700',
          )}
          title={detail}
        >
          {detail}
        </span>
      )}
      {row.screenshotSignedUrl && (
        <SpanScreenshotButton span={row} onSelect={onSelectScreenshot} />
      )}
      {showModel && row.model && (
        <span
          className="shrink-0 font-mono text-[10px] text-stone-400 truncate max-w-[140px]"
          title={row.model}
        >
          {row.model}
        </span>
      )}
      {row.sopStepMode && <SopModeBadge mode={row.sopStepMode} />}
      {row.sopVerify && <SopVerifyBadge verify={row.sopVerify} gt={gt} />}
    </>
  );
}

/**
 * What KIND of thing this row is, as a pill.
 *
 * Ordered most-specific first: a phase that spawned a child run or workflow is named for that,
 * then anything running its own LLM loop is a subagent — including a tool row with children, whose
 * children ARE that loop's calls.
 */
function SpanKindPill({
  row,
  gt,
}: {
  row: FlatRow;
  gt: ReturnType<typeof useGT>;
}) {
  const label = (): string => {
    if (row.human)
      return gt('Human', {
        context:
          'agent trace span type pill label, a person intervening in a live operator run',
      });
    if (row.childAgentRunId)
      return gt('Agent', {
        context:
          'agent trace span type pill label, task-loop phase spawning a child agent run',
      });
    if (row.childWorkflowRunId)
      return gt('Workflow', {
        context:
          'agent trace span type pill label, task-loop phase running a setup/teardown workflow',
      });
    // Who performed it, when not the main agent's own tool call: a replayed procedure (its row,
    // its steps' actions, and the agent's call to run it) or the procedure's healer.
    if (row.actor === 'healer')
      return gt('Healer', {
        context:
          'agent trace span type pill label, an action the procedure healer improvised after a step drifted',
      });
    if (row.sopStepIndex != null)
      return gt('Step', {
        context:
          'agent trace span type pill label, one step of a replayed procedure',
      });
    if (row.isSopProcedure || row.actor === 'procedure')
      return gt('Procedure', {
        context:
          'agent trace span type pill label, a replayed procedure (SOP) or an action it performed',
      });
    if (
      row.agentCard ||
      row.isSubagent ||
      (row.type === 'tool' && row.hasChildren)
    )
      return gt('Subagent', {
        context:
          'agent trace span type pill label, subagent whose handler runs its own LLM loop',
      });
    if (row.type === 'tool')
      return gt('Tool', {
        context: 'agent trace span type pill label, tool call',
      });
    return gt('LLM', { context: 'agent trace span type pill label, LLM step' });
  };
  return (
    <span className="text-[9.5px] font-semibold tracking-wider uppercase text-stone-500 px-1.5 py-0.5 border border-stone-200 rounded shrink-0 ml-auto">
      {label()}
    </span>
  );
}

/** The vertical rules that line a nested row up with its ancestors. */
function DepthGuides({ depth }: { depth: number }) {
  return (
    <>
      {Array.from({ length: depth }).map((_, i) => (
        <div
          key={i}
          className="absolute top-0 bottom-0 w-px bg-stone-200"
          style={{ left: `${12 + 8 + i * 18}px` }}
        />
      ))}
    </>
  );
}

/** Latency, with a mini bar placing this span inside the run's window. */
function LatencyCell({
  row,
  isTool,
  barStart,
  barWidth,
}: {
  row: FlatRow;
  isTool: boolean;
  barStart: number;
  barWidth: number;
}) {
  return (
    <td className="px-2 h-8 align-middle pr-3">
      <div className="flex flex-col gap-0.5">
        <SpanDuration
          span={row}
          className="text-[11.5px] font-mono tabular-nums text-stone-800"
        />
        {row.durMs != null && (
          <div className="relative h-1 bg-stone-100 rounded-sm overflow-hidden">
            <div
              className={cn(
                'absolute top-0 bottom-0 rounded-sm',
                row.status === 'failed'
                  ? 'bg-red-500'
                  : isTool
                    ? 'bg-stone-500'
                    : 'bg-stone-700',
              )}
              style={{ left: `${barStart}%`, width: `${barWidth}%` }}
            />
          </div>
        )}
      </div>
    </td>
  );
}

/** One right-aligned token count. Four of these differ only in tint. */
function TokenCell({ value, tint }: { value: number; tint: string }) {
  return (
    <td
      className={cn(
        'px-2 h-8 align-middle text-right font-mono text-[11.5px] tabular-nums',
        tint,
      )}
    >
      {fmtTokens(value)}
    </td>
  );
}

// --- SpanRow ---

interface SpanRowProps {
  row: FlatRow;
  isSelected: boolean;
  onSelect: (span: Span) => void;
  onSelectScreenshot?: (span: Span) => void;
  onToggle: (id: string) => void;
  expanded: Set<string>;
  query: string;
  totalDurMs: number;
}

// Was cyclomatic 20 / cognitive 28 / 142 lines. The kind pill, depth guides, latency bar and
// token cells are now their own components, leaving 10 / 12 / 78 — under both real ceilings.
// What remains is the estimated-coverage term of CRAP, not structure.
// fallow-ignore-next-line complexity
function SpanRow({
  row,
  isSelected,
  onSelect,
  onSelectScreenshot,
  onToggle,
  expanded,
  query,
  totalDurMs,
}: SpanRowProps) {
  const gt = useGT();
  const isExpanded = expanded.has(row.id);
  const isTool = row.type === 'tool';

  // Display the rolled-up totals (own + nested) so every row sums up its subtree;
  // falls back to the span's own counts for spans aggregateTokens didn't touch.
  const tok = row.rolled ?? {
    input: row.inputTokens ?? 0,
    output: row.outputTokens ?? 0,
    reasoning: row.reasoningTokens ?? 0,
    cacheRead: row.cacheReadTokens ?? 0,
    cacheWrite: row.cacheWriteTokens ?? 0,
  };

  const barStart = totalDurMs > 0 ? (row.startMs / totalDurMs) * 100 : 0;
  const barWidth =
    totalDurMs > 0 && row.durMs != null
      ? Math.max(0.5, (row.durMs / totalDurMs) * 100)
      : 0;

  return (
    <tr
      tabIndex={0}
      onClick={() => {
        if (!isSelectingText())
          activateRow(row, isExpanded, onSelect, onToggle);
      }}
      onKeyDown={(e) =>
        onActivateKey(e, () => activateRow(row, isExpanded, onSelect, onToggle))
      }
      className={cn(
        'border-b border-border cursor-pointer transition-colors duration-75',
        rowSurface(isSelected, isTool, !!row.human),
      )}
    >
      {/* Name */}
      <td className="p-0">
        <div
          className="flex items-center h-8 pr-2 gap-1.5 relative"
          style={{ paddingLeft: `${12 + row.depth * 18}px` }}
        >
          <DepthGuides depth={row.depth} />
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <SpanCaret
              row={row}
              isExpanded={isExpanded}
              onToggle={onToggle}
              gt={gt}
            />
            <SpanLabel
              row={row}
              query={query}
              showModel
              gt={gt}
              onSelectScreenshot={onSelectScreenshot}
            />
          </div>
          <SpanKindPill row={row} gt={gt} />
        </div>
      </td>

      {/* Status */}
      <td className="px-2 h-8 align-middle">
        <div className="flex items-center gap-1.5">
          <StatusDot status={row.status} />
          <span className="text-[11.5px] text-stone-700">{row.status}</span>
        </div>
      </td>

      <LatencyCell
        row={row}
        isTool={isTool}
        barStart={barStart}
        barWidth={barWidth}
      />
      <TokenCell value={tok.input} tint="text-stone-800" />
      <TokenCell value={tok.output} tint="text-stone-800" />
      <TokenCell value={tok.reasoning} tint="text-stone-500" />
      <TokenCell value={tok.cacheRead} tint="text-emerald-700" />
    </tr>
  );
}

/**
 * The brief a span was working from, on the face of its row.
 *
 * Styled apart from `ReasoningLine` on purpose: this is what the agent was GIVEN, not words it
 * produced, and running the two together would attribute the orders to whoever followed them.
 * Tinted and labelled rather than italic-grey, so the eye reads it as a brief.
 *
 * A work item's headline is a short title, always shown, with its long `body` behind a disclosure.
 * An instruction has no body — its headline is the whole brief and can run to dozens of sub-steps —
 * so it shows in full by default yet folds to a preview once it would swallow the rail.
 */
function BriefLine({
  brief,
  gt,
}: {
  brief: SpanBrief;
  gt: ReturnType<typeof useGT>;
}) {
  const label =
    brief.kind === 'work_item'
      ? gt('Working on', {
          context:
            'label on a trace row for the work item the agent picked up off its queue',
        })
      : gt('Instruction', {
          context:
            'label on a trace row for the natural-language instruction a subagent was given',
        });

  return (
    <div className="mb-1 ml-5 w-[calc(100%-2rem)] rounded-sm border-0 border-l-2 border-solid border-l-stone-300 bg-stone-100/70 px-2 py-1">
      {brief.kind === 'instruction' ? (
        <BriefInstruction label={label} text={brief.headline} gt={gt} />
      ) : (
        <WorkItemBrief label={label} brief={brief} gt={gt} />
      )}
    </div>
  );
}

/** The small monospace tag naming a brief — "INSTRUCTION", "WORKING ON". */
function BriefLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="mr-1 font-mono text-[9.5px] uppercase tracking-wide text-stone-500">
      {children}
    </span>
  );
}

/**
 * How tall a block of prose may grow before it offers to fold, in px. Low enough that even a
 * handful of lines earns the fold, yet well above the folded preview so the toggle always hides
 * something worth the click. Shared by an operator's instruction and a span's reasoning.
 */
const PROSE_FOLD_PX = 96;
/**
 * What an over-long block keeps on screen once folded: a two-line preview with `line-clamp` trailing
 * it in an ellipsis, so the fold reads as truncation rather than text that simply stops.
 */
const PROSE_FOLDED_CLASS = 'line-clamp-2';

/**
 * Long prose on a row — an operator's instruction, a span's reasoning — shown in full by default
 * but foldable to a preview once it would take over the rail. Expanded by default on purpose: the
 * rail reads top-to-bottom without a click, and the fold exists only to tame the occasional wall of
 * text, never to bury the words behind a toggle no one thinks to press. A block that already fits
 * gets no toggle at all.
 */
function FoldableProse({
  children,
  measureKey,
  gt,
}: {
  children: React.ReactNode;
  /** Re-runs the height check when the text changes — a live run can grow a block on the same row. */
  measureKey: string;
  gt: ReturnType<typeof useGT>;
}) {
  const [expanded, setExpanded] = useState(true);
  const [foldable, setFoldable] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const folded = foldable && !expanded;

  // The live `folded` state, read inside the observer below without re-subscribing it each fold.
  const foldedRef = useRef(folded);
  foldedRef.current = folded;

  // Measuring rendered height is DOM synchronization — the one place here an effect belongs. A
  // ResizeObserver keeps the toggle honest when the rail re-wraps (a viewport or column resize
  // changes scrollHeight without the text changing); `measureKey` re-runs it when a live run swaps
  // the text itself. We skip measuring while folded: a line-clamped element can report its clamped
  // height, which would wrongly retract the toggle (and thrash the observer) — and a block that is
  // already folded is known to overflow.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      if (!foldedRef.current) setFoldable(el.scrollHeight > PROSE_FOLD_PX);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measureKey]);

  return (
    <>
      <div ref={ref} className={cn(folded && PROSE_FOLDED_CLASS)}>
        {children}
      </div>
      {foldable && (
        <button
          type="button"
          // Folding is not selecting the span — stop the row's own click/keydown handlers from
          // firing underneath. See SpanCaret for why the keydown needs its own guard.
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          aria-expanded={expanded}
          className="mt-1 block cursor-pointer border-none bg-transparent p-0 font-mono text-[9.5px] uppercase tracking-wide text-stone-500 underline"
        >
          {expanded
            ? gt('Show less', {
                context:
                  'button collapsing a long block of text on a trace row',
              })
            : gt('Show more', {
                context: 'button expanding a long block of text on a trace row',
              })}
        </button>
      )}
    </>
  );
}

/**
 * An operator's instruction: a labelled brief that folds when it runs long. The instruction is the
 * whole brief and can run to dozens of sub-steps, so it earns the fold a short work-item title does
 * not.
 */
function BriefInstruction({
  label,
  text,
  gt,
}: {
  label: string;
  text: string;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <FoldableProse measureKey={text} gt={gt}>
      <BriefLabel>{label}</BriefLabel>
      <span className="whitespace-pre-wrap text-[11.5px] font-medium leading-relaxed text-stone-800">
        {text}
      </span>
    </FoldableProse>
  );
}

/**
 * A work item's brief: a short title, plus its longer `body` behind a "Details" disclosure. The
 * brief renderer as it was before instructions grew their own folding — unchanged, because a work
 * item's headline is a line, not a wall of steps.
 */
function WorkItemBrief({
  label,
  brief,
  gt,
}: {
  label: string;
  brief: SpanBrief;
  gt: ReturnType<typeof useGT>;
}) {
  const [open, setOpen] = useState(false);
  const body = brief.body?.trim() || null;

  return (
    <>
      <BriefLabel>{label}</BriefLabel>
      <span className="whitespace-pre-wrap text-[11.5px] font-medium leading-relaxed text-stone-800">
        {brief.headline}
      </span>
      {body && (
        <button
          type="button"
          // Reading the description is not selecting the span — without this the row's own
          // handler fires too and the detail panel opens over the thing being read. See
          // SpanCaret for why the keydown needs its own guard.
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          aria-expanded={open}
          className="ml-1.5 cursor-pointer border-none bg-transparent p-0 font-mono text-[9.5px] uppercase tracking-wide text-stone-500 underline"
        >
          {open
            ? gt('Hide details', {
                context:
                  'button collapsing a work item’s description on a trace row',
              })
            : gt('Details', {
                context:
                  'button revealing a work item’s description on a trace row',
              })}
        </button>
      )}
      {body && open && (
        <p className="mb-0 mt-1 whitespace-pre-wrap text-[11.5px] leading-relaxed text-stone-700">
          {body}
        </p>
      )}
    </>
  );
}

/**
 * A span's reasoning — the model's own "why did it do that" — on its rail beneath the row. Shown in
 * full by default so the trace reads top-to-bottom, but a chatty run can produce a wall of text that
 * swallows the rail, so it folds like an instruction once it runs long.
 */
function ReasoningLine({
  text,
  gt,
}: {
  text: string;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <div
      // `border-0 border-l-2 border-solid`, not `border-none`: `border-none` sets
      // border-style:none, which leaves the 2px left rule sized but invisible.
      className="mb-1 ml-5 w-[calc(100%-2rem)] border-0 border-l-2 border-solid border-l-stone-200 pl-2 text-left"
    >
      <FoldableProse measureKey={text} gt={gt}>
        <span className="whitespace-pre-wrap text-[11.5px] leading-relaxed text-stone-700">
          {text}
        </span>
      </FoldableProse>
    </div>
  );
}

/** The row's resting surface: selected wins, then a human's row gets the amber tint the
 * takeover controls use (so the two read as one event), then tool rows the warm one. */
function rowSurface(
  isSelected: boolean,
  isTool: boolean,
  isHuman = false,
): string {
  if (isSelected)
    return 'bg-stone-100 shadow-[inset_2px_0_0_var(--color-stone-500)]';
  if (isHuman) return 'bg-amber-50 hover:bg-amber-100/70';
  return isTool
    ? 'bg-[#fbf8f1] hover:bg-[#f6f1e2]'
    : 'bg-white hover:bg-stone-50';
}

/** The row name of a human intervention — the person's act, not a tool. */
function humanRowName(gt: ReturnType<typeof useGT>): string {
  return gt('Human takeover', {
    context:
      'agent trace row name for a person pausing and stepping into a live operator run',
  });
}

/** The chip on a human row: how the pause ended (or that it is still open). */
function humanOutcomeLabel(
  human: SpanHumanTakeover,
  gt: ReturnType<typeof useGT>,
): string {
  switch (human.outcome) {
    case 'paused':
      return gt('waiting…', {
        context:
          'agent trace chip on a human-takeover row while the operator is paused for the person',
      });
    case 'instruct':
      return gt('instruction', {
        context:
          'agent trace chip on a human-takeover row: the person typed an instruction',
      });
    case 'takeover':
      return gt('drove desktop', {
        context:
          'agent trace chip on a human-takeover row: the person drove the remote desktop',
      });
    case 'cancelled':
      return gt('stopped', {
        context:
          'agent trace chip on a human-takeover row: the run was stopped while paused',
      });
    case 'timed_out':
      return gt('unanswered', {
        context:
          'agent trace chip on a human-takeover row: nobody answered before the pause timed out',
      });
  }
}

/**
 * What the person did, under a human-takeover row — in place of `ReasoningLine`, because these
 * are a human's words and acts, not the agent's rationale, and the two must not read alike.
 * Amber rail to match the row's tint; the instruction itself is quoted verbatim.
 */
function HumanTakeoverLine({
  human,
  gt,
}: {
  human: SpanHumanTakeover;
  gt: ReturnType<typeof useGT>;
}) {
  const who =
    human.byName ??
    gt('A human', {
      context:
        'fallback subject for a human-takeover trace line when the person is unknown',
    });
  const sentence = (): string => {
    switch (human.outcome) {
      case 'paused':
        return gt(
          'Paused — a person is deciding what the operator should do next.',
          {
            context:
              'agent trace line under a human-takeover row while the pause is still open',
          },
        );
      case 'instruct':
        return human.instruction
          ? gt('{who}: “{instruction}”', {
              who,
              instruction: human.instruction,
              context:
                'agent trace line under a human-takeover row quoting the instruction the person typed',
            })
          : gt('{who} sent the operator an instruction.', {
              who,
              context:
                'agent trace line under a human-takeover row when the instruction text was not recorded',
            });
      case 'takeover':
        return gt('{who} drove the remote desktop, then handed control back.', {
          who,
          context:
            'agent trace line under a human-takeover row after the person drove the desktop',
        });
      case 'cancelled':
        return gt('The run was stopped while it was paused.', {
          context:
            'agent trace line under a human-takeover row when the run was cancelled mid-pause',
        });
      case 'timed_out':
        return gt(
          'Nobody answered within the pause window; the operator gave up waiting.',
          {
            context:
              'agent trace line under a human-takeover row when the pause timed out',
          },
        );
    }
  };
  return (
    <div className="mb-1 ml-5 w-[calc(100%-2rem)] border-0 border-l-2 border-solid border-l-amber-400 pl-2 text-left">
      <FoldableProse measureKey={human.instruction ?? human.outcome} gt={gt}>
        <span className="whitespace-pre-wrap text-[11.5px] leading-relaxed text-amber-950">
          {sentence()}
        </span>
      </FoldableProse>
    </div>
  );
}

/**
 * The row-body cursor. Only a collapsed parent does anything on a click now (it expands); every
 * other row reads as static text and should not wear a pointer it can't honour.
 */
function rowCursor(row: FlatRow, isExpanded: boolean): string {
  return row.hasChildren && !isExpanded ? 'cursor-pointer' : 'cursor-default';
}

/**
 * What clicking (or Enter/Space on) a row does: a COLLAPSED parent opens, nothing more — the
 * first click is asking "what's in here?", and answering it with the detail panel buried the
 * children it just revealed. Only a leaf or an already-open parent selects. Collapsing stays on
 * the caret, so reading an open subtree never folds it by accident.
 */
function activateRow(
  row: FlatRow,
  isExpanded: boolean,
  onSelect: (span: Span) => void,
  onToggle: (id: string) => void,
): void {
  if (row.hasChildren && !isExpanded) {
    onToggle(row.id);
    return;
  }
  onSelect(row);
}

/**
 * A click that ends a drag-select inside a row is someone copying text, not asking to open or
 * expand it — activating would swap the panel out from under the selection they just made.
 */
function isSelectingText(): boolean {
  return Boolean(window.getSelection()?.toString());
}

/** Enter/Space activate a treeitem, the way a native control would. */
function onActivateKey(e: React.KeyboardEvent, activate: () => void): void {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  activate();
}

/**
 * A span's full detail, opened in place inside the treeitem so it stays anchored to its row.
 *
 * Its clicks are stopped: the copy/expand controls inside must not re-fire the row's select
 * handler, which would close the very block being used.
 */
function RowDetailBlock({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="pb-1 pr-2"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

/**
 * What clicking (or Enter/Space on) a compact list row does: expand a COLLAPSED parent, nothing
 * more. Opening a span's detail used to ride on the same click, but the whole row was too large a
 * target for it — reading the trace kept springing a metadata panel open by accident. That now
 * lives on an explicit control ({@link DetailToggleButton}); the row body is just for reading.
 */
function expandRowOnActivate({
  row,
  isExpanded,
  onToggle,
}: {
  row: FlatRow;
  isExpanded: boolean;
  onToggle: (id: string) => void;
}): void {
  if (row.hasChildren && !isExpanded) onToggle(row.id);
}

/**
 * The control that opens a span's detail in place — a small "more" target at the end of the row.
 * Split off from the row body on purpose: selecting a span pins a metadata panel beneath it, and
 * when the entire row did that a stray click kept burying the trace under a panel.
 */
function DetailToggleButton({
  isOpen,
  onToggle,
  gt,
}: {
  isOpen: boolean;
  onToggle: () => void;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <button
      type="button"
      // The row body handles expand; this must not bubble to it, or opening the detail would also
      // toggle the subtree underneath. See SpanCaret for why the keydown needs its own guard.
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      aria-expanded={isOpen}
      aria-label={gt('Span details', {
        context: 'button opening the metadata panel for a trace span',
      })}
      className={cn(
        'flex shrink-0 cursor-pointer items-center rounded border-none bg-transparent p-0.5 text-stone-400 transition-colors hover:bg-stone-200/70 hover:text-stone-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        isOpen && 'bg-stone-200 text-stone-700',
      )}
    >
      <DotsThreeVerticalIcon size={16} weight="bold" />
    </button>
  );
}

// --- SpanListRow ---

function trimmedReasoning(reasoning: string | undefined | null) {
  return reasoning?.trim() || null;
}

function activateCompactRow(
  row: FlatRow,
  action: 'expand' | 'inspect',
  isExpanded: boolean,
  onSelect: (span: Span) => void,
  onToggle: (id: string) => void,
) {
  if (action === 'inspect') onSelect(row);
  else expandRowOnActivate({ row, isExpanded, onToggle });
}

function onCompactRowKey(
  e: React.KeyboardEvent,
  action: 'expand' | 'inspect',
  activate: () => void,
) {
  if (action === 'expand' || e.target === e.currentTarget)
    onActivateKey(e, activate);
}

function compactRowCursor(
  row: FlatRow,
  isExpanded: boolean,
  action: 'expand' | 'inspect',
) {
  return action === 'inspect' ? 'cursor-pointer' : rowCursor(row, isExpanded);
}

/**
 * One span as a variable-height list row, used in `compact` mode.
 *
 * The wide table can't do this: `tableLayout: fixed` needs every row the same height, so an
 * inline reasoning line has nowhere to go and the "why" ends up hidden behind a click. Here the
 * row is a flex block, so each span can carry the model's own words directly beneath it and the
 * trace reads top-to-bottom as a narrative instead of a call log.
 */
function SpanListRow({
  compactRowAction,
  row,
  isSelected,
  onSelect,
  onSelectScreenshot,
  onToggle,
  expanded,
  query,
  gt,
  detail,
  focusRequest,
  clock,
}: {
  compactRowAction: 'expand' | 'inspect';
  row: FlatRow;
  isSelected: boolean;
  onSelect: (span: Span) => void;
  onSelectScreenshot?: (span: Span) => void;
  onToggle: (id: string) => void;
  expanded: Set<string>;
  query: string;
  gt: ReturnType<typeof useGT>;
  /** The span's full detail, opened in place beneath the row. */
  detail?: React.ReactNode;
  /** A request to scroll some row into view; acted on only when it names THIS row. */
  focusRequest?: { id: string } | null;
  /** The run's wall clock, so the row can say when it started. */
  clock?: RunClock | null;
}) {
  const isExpanded = expanded.has(row.id);
  const isTool = row.type === 'tool';
  const reasoning = trimmedReasoning(row.reasoning);
  const brief = row.brief ?? null;
  const rowRef = useRef<HTMLDivElement>(null);
  const isFocusTarget = focusRequest?.id === row.id;

  // Scrolling is DOM synchronization, so an effect is the right tool. Keyed on the request OBJECT,
  // not the id: asking for the same row twice must scroll again, and callers mint a fresh object
  // per request precisely so it can.
  useEffect(() => {
    if (isFocusTarget) revealInScroller(rowRef.current);
  }, [isFocusTarget, focusRequest]);

  function activate() {
    activateCompactRow(row, compactRowAction, isExpanded, onSelect, onToggle);
  }

  return (
    // Role, ARIA state, and the focus target are all on THIS element: a screen reader that lands
    // on a focusable inner div would announce a nameless, stateless node while the treeitem
    // semantics sat on its unfocusable parent.
    <div
      ref={rowRef}
      role="treeitem"
      aria-level={row.depth + 1}
      aria-selected={isSelected}
      aria-expanded={row.hasChildren ? isExpanded : undefined}
      tabIndex={0}
      onClick={() => {
        if (!isSelectingText()) activate();
      }}
      onKeyDown={(e) => onCompactRowKey(e, compactRowAction, activate)}
      className={cn(
        'border-b border-border outline-none focus-visible:ring-1 focus-visible:ring-ring',
        compactRowCursor(row, isExpanded, compactRowAction),
        rowSurface(isSelected, isTool, !!row.human),
      )}
      style={{ paddingLeft: `${8 + Math.min(row.depth, 5) * 14}px` }}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-1.5 py-1 pr-2">
        <SpanCaret
          row={row}
          isExpanded={isExpanded}
          onToggle={onToggle}
          gt={gt}
        />
        <SpanLabel
          row={row}
          query={query}
          showModel={false}
          gt={gt}
          onSelectScreenshot={onSelectScreenshot}
        />
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          <SpanStartTime clock={clock} span={row} />
          <StatusDot status={row.status} />
          {row.status === 'running' && (
            <span className="text-[10.5px] font-medium text-amber-700">
              {gt('Running', {
                context:
                  'agent trace compact row status, the span is still in progress',
              })}
            </span>
          )}
          <SpanDuration
            span={row}
            className="font-mono text-[10.5px] tabular-nums text-stone-500"
          />
          <DetailToggleButton
            isOpen={isSelected}
            onToggle={() => onSelect(row)}
            gt={gt}
          />
        </span>
      </div>

      {/* The brief first, then what the agent made of it — the order the run happened in. */}
      {brief && <BriefLine brief={brief} gt={gt} />}
      {reasoning && <ReasoningLine text={reasoning} gt={gt} />}
      {row.human && <HumanTakeoverLine human={row.human} gt={gt} />}

      {detail && <RowDetailBlock>{detail}</RowDetailBlock>}
    </div>
  );
}

// --- FilterBar ---

/** Filter controls the table owns and hands straight through to the bar. */
interface TraceFilterControls {
  query: string;
  onQueryChange: (q: string) => void;
  filters: TraceFilters;
  typeCounts: Record<SpanType, number>;
  onToggleType: (t: SpanType) => void;
  onToggleStatus: (s: SpanStatus) => void;
  onSetMinTokens: (n: number) => void;
  onClearFilters: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  showFilters: boolean;
  onToggleShowFilters: () => void;
  /** The run is still active, so the timeline is polling — surfaces a live badge. */
  isLive?: boolean;
  /** A poll is in flight right now. */
  isFetching?: boolean;
  pollIntervalMs?: number;
}

type FilterBarProps = TraceFilterControls;

const SPAN_STATUS_VALUES: SpanStatus[] = ['completed', 'running', 'failed'];

function FilterBar({
  query,
  onQueryChange,
  filters,
  typeCounts,
  onToggleType,
  onToggleStatus,
  onSetMinTokens,
  onClearFilters,
  onExpandAll,
  onCollapseAll,
  showFilters,
  onToggleShowFilters,
  isLive = false,
  isFetching = false,
  pollIntervalMs = 0,
}: FilterBarProps) {
  const gt = useGT();
  const statusChipLabel = {
    completed: gt('completed', {
      context: 'agent trace span status filter chip',
    }),
    running: gt('running', {
      context: 'agent trace span status filter chip',
    }),
    failed: gt('failed', {
      context: 'agent trace span status filter chip',
    }),
  } satisfies Record<SpanStatus, string>;
  const activeCount =
    filters.types.size +
    filters.statuses.size +
    (filters.minTokens > 0 ? 1 : 0);

  return (
    <div className="border-b border-border px-3 py-2 bg-white flex flex-col gap-2 shrink-0">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-sm">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={gt('Search spans…', {
              context: 'agent trace filter bar search input placeholder',
            })}
            className="w-full h-7 pl-7 pr-7 text-xs bg-stone-50 border border-border rounded-md font-sans text-stone-900 outline-none focus:ring-1 focus:ring-ring"
          />
          {query && (
            <button
              onClick={() => onQueryChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 border-none bg-transparent text-muted-foreground cursor-pointer p-0"
            >
              <XIcon className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="w-px h-4 bg-border" />

        <button
          onClick={onToggleShowFilters}
          className={cn(
            'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs text-stone-800 cursor-pointer transition-colors',
            showFilters
              ? 'bg-stone-100 border-border'
              : 'bg-white border-border hover:bg-stone-50',
          )}
        >
          <FunnelIcon className="h-3 w-3" />
          {gt('Filters', {
            context: 'agent trace filter bar toggle button label',
          })}
          {activeCount > 0 && (
            <span className="px-1.5 bg-stone-800 text-white rounded-full text-[10px] font-semibold">
              {activeCount}
            </span>
          )}
        </button>

        <div className="flex-1" />

        {isLive && (
          <LivePill intervalMs={pollIntervalMs} isFetching={isFetching} />
        )}

        <button
          onClick={onExpandAll}
          className="h-6 px-2 border-none bg-transparent text-[11.5px] text-stone-700 cursor-pointer rounded hover:bg-stone-100"
        >
          {gt('Expand all', {
            context: 'agent trace button to expand all span rows',
          })}
        </button>
        <button
          onClick={onCollapseAll}
          className="h-6 px-2 border-none bg-transparent text-[11.5px] text-stone-700 cursor-pointer rounded hover:bg-stone-100"
        >
          {gt('Collapse all', {
            context: 'agent trace button to collapse all span rows',
          })}
        </button>
      </div>

      {showFilters && (
        <div className="flex items-center gap-3 flex-wrap pt-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[10.5px] font-semibold tracking-wider uppercase text-stone-500">
              {gt('Type', {
                context: 'agent trace filter bar type filter group label',
              })}
            </span>
            <FilterChip
              active={filters.types.has('llm')}
              onClick={() => onToggleType('llm')}
              count={typeCounts.llm}
            >
              <BrainIcon className="h-2.5 w-2.5" />
              {gt('LLM', {
                context: 'agent trace span type filter chip, LLM step',
              })}
            </FilterChip>
            <FilterChip
              active={filters.types.has('tool')}
              onClick={() => onToggleType('tool')}
              count={typeCounts.tool}
            >
              <WrenchIcon className="h-2.5 w-2.5" />
              {gt('Tool', {
                context: 'agent trace span type filter chip, tool call',
              })}
            </FilterChip>
          </div>

          <div className="w-px h-4 bg-border" />

          <div className="flex items-center gap-1.5">
            <span className="text-[10.5px] font-semibold tracking-wider uppercase text-stone-500">
              {gt('Status', {
                context: 'agent trace filter bar status filter group label',
              })}
            </span>
            {SPAN_STATUS_VALUES.map((s) => (
              <FilterChip
                key={s}
                active={filters.statuses.has(s)}
                onClick={() => onToggleStatus(s)}
              >
                <StatusDot status={s} />
                {statusChipLabel[s]}
              </FilterChip>
            ))}
          </div>

          <div className="w-px h-4 bg-border" />

          <div className="flex items-center gap-1.5">
            <span className="text-[10.5px] font-semibold tracking-wider uppercase text-stone-500">
              {gt('Min tokens', {
                context:
                  'agent trace filter bar minimum token count filter label',
              })}
            </span>
            <select
              value={filters.minTokens}
              onChange={(e) => onSetMinTokens(parseInt(e.target.value))}
              className="h-6 px-1.5 border border-border rounded-md text-[11.5px] bg-white text-stone-800 cursor-pointer font-mono"
            >
              <option value={0}>
                {gt('any', {
                  context: 'agent trace min tokens filter option, no minimum',
                })}
              </option>
              <option value={1000}>
                {gt('≥ 1k', {
                  context:
                    'agent trace min tokens filter option, at least 1000 tokens',
                })}
              </option>
              <option value={5000}>
                {gt('≥ 5k', {
                  context:
                    'agent trace min tokens filter option, at least 5000 tokens',
                })}
              </option>
              <option value={10000}>
                {gt('≥ 10k', {
                  context:
                    'agent trace min tokens filter option, at least 10000 tokens',
                })}
              </option>
              <option value={50000}>
                {gt('≥ 50k', {
                  context:
                    'agent trace min tokens filter option, at least 50000 tokens',
                })}
              </option>
            </select>
          </div>

          {activeCount > 0 && (
            <button
              onClick={onClearFilters}
              className="h-6 px-2 border-none bg-transparent text-[11.5px] text-red-600 cursor-pointer rounded hover:bg-red-50"
            >
              {gt('Clear filters', {
                context: 'agent trace button to reset all active filters',
              })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// --- SpanTable ---

interface SpanTableProps extends TraceFilterControls {
  /**
   * What a click on a compact row body does. `inspect` selects the span (its detail opens in the
   * host's side pane; the caret still expands). Opt in only for a host with an adjacent evidence
   * inspector — with in-place `renderDetail`, a whole-row target kept burying the trace.
   */
  compactRowAction?: 'expand' | 'inspect';
  spans: Span[];
  totalDurMs: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  selected: Span | null;
  onSelect: (span: Span) => void;
  onSelectScreenshot?: (span: Span) => void;
  /**
   * Render as a narrative list instead of the wide table: name / status / latency per row, with
   * the span's own reasoning inline beneath it.
   *
   * Not just a narrower table. `tableLayout: fixed` fixes every row to one height, so reasoning
   * text has nowhere to live and the "why" gets exiled to a detail pane. The list drops the token
   * columns (they don't fit a rail anyway) and spends the space on the agent's words instead.
   *
   * An explicit prop rather than a Tailwind breakpoint because the constraint is the CONTAINER's
   * width, not the viewport's — this renders inside a resizable panel that can be a third of a
   * wide screen, where every `xl:` would still read as "plenty of room".
   */
  compact?: boolean;
  /**
   * Render a span's full detail in place, directly beneath its row, instead of leaving the caller
   * to open it somewhere else. Called only for the selected row, and only in `compact` mode — the
   * wide table's fixed layout has no room for a variable-height block, and it already has a
   * side-by-side detail pane.
   */
  renderDetail?: (span: Span) => React.ReactNode;
  /**
   * Scroll a row into view. Pass a NEW object per request — repeating the same id must scroll
   * again, which an id-only prop can't express. `compact` mode only.
   */
  focusRequest?: { id: string } | null;
  /**
   * Render the search / filter / expand-all bar. Default true.
   *
   * Worth turning off while a run is LIVE: the tree is a handful of rows and still growing, so
   * there is nothing to search yet, and a filter narrows a set the next poll changes underneath
   * it. Once the run is a finished log, querying it is the point.
   *
   * Hiding the bar also stops it filtering — a control nobody can see must not still be culling
   * rows, which is what someone who filtered a completed run and then restarted it would hit.
   */
  showFilterBar?: boolean;
  /** The run's wall clock; when given, each list row shows the time it started. */
  clock?: RunClock | null;
}

/**
 * How far from the bottom still counts as "watching the tail".
 *
 * Not zero: a row's reasoning wraps to a variable height, so the resting scroll position after a
 * poll is rarely exactly the bottom, and an exact test would stop following almost immediately.
 */
const FOLLOW_TAIL_SLACK_PX = 120;

const ALIGN_LEFT = 'left' as const;
const ALIGN_RIGHT = 'right' as const;

// SpanTable uses useGT internally for column headers and empty state
export function SpanTable({
  compactRowAction = 'expand',
  spans,
  totalDurMs,
  expanded,
  onToggle,
  selected,
  onSelect,
  onSelectScreenshot,
  query,
  onQueryChange,
  filters,
  typeCounts,
  onToggleType,
  onToggleStatus,
  onSetMinTokens,
  onClearFilters,
  onExpandAll,
  onCollapseAll,
  showFilters,
  onToggleShowFilters,
  isLive,
  isFetching,
  pollIntervalMs,
  compact = false,
  renderDetail,
  focusRequest = null,
  showFilterBar = true,
  clock,
}: SpanTableProps) {
  const gt = useGT();
  const filtered = useMemo(
    () => (showFilterBar ? filterTree(spans, filters, query) : spans),
    [spans, filters, query, showFilterBar],
  );
  const rows = useMemo(
    () => flattenSpans(filtered, 0, expanded, []),
    [filtered, expanded],
  );
  // Rows are keyed, and expanded, by span id: a repeated id renders a stale row and toggles the
  // wrong subtree. Loud in development, free in production.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const repeated = duplicateSpanIds(spans);
    if (repeated.length > 0) {
      console.warn('SpanTable: duplicate span ids', repeated);
    }
  }, [spans]);

  const treeRef = useRef<HTMLDivElement>(null);
  // Pin to the tail of a live run, the way a log viewer does: new rows land at the bottom every
  // poll, and without this someone watching an agent work has to keep scrolling to stay with it.
  //
  // Sticky, NOT proximity-based. Testing "is the view already near the bottom?" at each update
  // reads as the same thing but isn't: the rail opens scrolled to the top, so on any run long
  // enough to overflow, the check fails on the first poll and following never starts at all. So
  // following is ON by default and only a scroll AWAY from the bottom turns it off.
  const following = useRef(true);
  const handleScroll = () => {
    const el = treeRef.current;
    if (!el) return;
    // Our own scroll-to-bottom lands within the slack, so it re-arms rather than cancelling
    // itself; scrolling back down to the bottom by hand resumes following for the same reason.
    following.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_TAIL_SLACK_PX;
  };

  // Keyed on the LAST row's identity and the length of its reasoning, not on `rows` or a row
  // count. Two things have to pull the view down — a new row arriving, and the bottom row's prose
  // growing as it streams — while expanding a row higher up must NOT, even though that also
  // changes the row count.
  const tail = rows[rows.length - 1];
  const tailSignature = `${tail?.id ?? ''}:${tail?.reasoning?.length ?? 0}`;
  useEffect(() => {
    const el = treeRef.current;
    if (!el || !compact || !isLive || !following.current) return;
    el.scrollTop = el.scrollHeight;
  }, [tailSignature, compact, isLive]);

  // A finished run is a document to read from the top; a fresh live one is a feed to follow.
  useEffect(() => {
    if (isLive) following.current = true;
  }, [isLive]);

  const tableColumns = useMemo(
    () => [
      {
        key: 'name',
        width: null,
        label: gt('Name', { context: 'agent trace table column header' }),
        align: ALIGN_LEFT,
      },
      {
        key: 'status',
        width: '96px',
        label: gt('Status', { context: 'agent trace table column header' }),
        align: ALIGN_LEFT,
      },
      {
        key: 'latency',
        width: '110px',
        label: gt('Latency', { context: 'agent trace table column header' }),
        align: ALIGN_LEFT,
      },
      {
        key: 'input',
        width: '62px',
        label: gt('Input', {
          context: 'agent trace table column header, input token count',
        }),
        align: ALIGN_RIGHT,
      },
      {
        key: 'output',
        width: '62px',
        label: gt('Output', {
          context: 'agent trace table column header, output token count',
        }),
        align: ALIGN_RIGHT,
      },
      {
        key: 'reasoning',
        width: '72px',
        label: gt('Reasoning', {
          context:
            'agent trace table column header, reasoning/thinking token count',
        }),
        align: ALIGN_RIGHT,
      },
      {
        key: 'cache',
        width: '62px',
        label: gt('Cache', {
          context: 'agent trace table column header, cache read token count',
        }),
        align: ALIGN_RIGHT,
      },
    ],
    [gt],
  );

  return (
    <div className="flex flex-col min-w-0 h-full overflow-hidden">
      {showFilterBar && (
        <FilterBar
          query={query}
          onQueryChange={onQueryChange}
          filters={filters}
          typeCounts={typeCounts}
          onToggleType={onToggleType}
          onToggleStatus={onToggleStatus}
          onSetMinTokens={onSetMinTokens}
          onClearFilters={onClearFilters}
          onExpandAll={onExpandAll}
          onCollapseAll={onCollapseAll}
          showFilters={showFilters}
          onToggleShowFilters={onToggleShowFilters}
          isLive={isLive}
          isFetching={isFetching}
          pollIntervalMs={pollIntervalMs}
        />
      )}
      {compact ? (
        <div
          ref={treeRef}
          onScroll={handleScroll}
          role="tree"
          aria-label={gt('Trace spans', {
            context: 'accessible label for the agent trace span tree',
          })}
          className="flex-1 overflow-auto"
        >
          {rows.map((row) => (
            <SpanListRow
              compactRowAction={compactRowAction}
              key={row.id}
              row={row}
              isSelected={selected?.id === row.id}
              onSelect={onSelect}
              onSelectScreenshot={onSelectScreenshot}
              onToggle={onToggle}
              expanded={expanded}
              query={query}
              gt={gt}
              detail={selected?.id === row.id ? renderDetail?.(row) : undefined}
              focusRequest={focusRequest}
              clock={clock}
            />
          ))}
          {rows.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {gt('No spans match your filters.', {
                context:
                  'agent trace table empty state when filters exclude all rows',
              })}
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-auto flex-1">
          <table
            className="w-full border-collapse"
            style={{ minWidth: '1000px', tableLayout: 'fixed' }}
          >
            <colgroup>
              {tableColumns.map((col) => (
                <col
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr>
                {tableColumns.map((col) => (
                  <th
                    key={col.key}
                    className="px-2 py-2 text-[10px] font-semibold uppercase tracking-widest text-stone-500 border-b border-border bg-stone-50 h-[30px]"
                    style={{ textAlign: col.align }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <SpanRow
                  key={row.id}
                  row={row}
                  isSelected={selected?.id === row.id}
                  onSelect={onSelect}
                  onSelectScreenshot={onSelectScreenshot}
                  onToggle={onToggle}
                  expanded={expanded}
                  query={query}
                  totalDurMs={totalDurMs}
                />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={tableColumns.length}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    {gt('No spans match your filters.', {
                      context:
                        'agent trace table empty state when filters exclude all rows',
                    })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
